import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TablePaginationActions } from '~/src/components/molecules'

interface IDataTable {
  id: number
  userId: string
  userName: string
  status?: number
}
interface IPagination {
  currentPage: number
  limitValue: number
  totalCount: number
  totalPages: number
}

interface Props {
  data: IDataTable[]
  pagination: IPagination
  onPageChange: (page: number) => void
  onDelete: (id: number, name: string) => void
}

interface Column {
  id: 'userId' | 'userName' | 'status' | 'edit'
  label: string
  minWidth?: number
  align?: 'left'
  render: (item: IDataTable, index: number) => JSX.Element
}

export const TableListUser: FC<Props> = ({
  data,
  pagination,
  onPageChange,
  onDelete,
}): JSX.Element => {
  const { t } = useTranslation()
  const [page, setPage] = useState(pagination.currentPage - 1)
  const handleChangePage = (event: unknown, newPage: number): void => {
    console.log({ event, newPage })
    setPage(newPage)
    onPageChange(newPage)
  }

  const columns: Column[] = [
    {
      id: 'userId',
      label: `${t('user_list.user_id')}`,
      minWidth: 170,
      render: ({ userId }) => (
        <Typography
          sx={{
            fontWeight: 'Regular',
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: ' 0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {userId}
        </Typography>
      ),
    },
    {
      id: 'userName',
      label: `${t('user_list.user_name')}`,
      minWidth: 100,
      render: ({ userName }) => (
        <Typography
          sx={{
            fontWeight: 'Regular',
            fontStyle: 'normal',
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: ' 0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {userName}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: `${t('user_list.status')}`,
      minWidth: 170,
      render: ({ status }) => (
        <Typography
          sx={{
            fontWeight: 'Regular',
            fontStyle: 'normal',
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: ' 0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {status}
        </Typography>
      ),
    },
    {
      id: 'edit',
      label: `${t('user_list.edit')}`,
      minWidth: 170,
      render: ({ id, userName }) => (
        <Button
          sx={{
            fontWeight: 'Regular',
            fontStyle: 'normal',
            fontSize: 14,
            color: '#F23D3D',
            textAlign: 'center',
            lineHeight: '20px',
            letterSpacing: ' 0.15px',
          }}
          onClick={() => onDelete(id, userName)}
        >
          {t('user_list.delete')}
        </Button>
      ),
    },
  ]

  useEffect(() => {
    setPage(pagination.currentPage - 1)
  }, [pagination.currentPage])

  return (
    <TableContainer component={Paper} sx={{ width: '100%', mt: '22px', padding: 1 }}>
      <Table
        sx={{ minWidth: 750 }}
        aria-label="custom pagination table sticky table"
        aria-labelledby="tableTitle"
        size={'medium'}
        stickyHeader
      >
        <TableHead>
          <TableRow>
            {columns.map((headCell, index) => (
              <TableCell
                key={headCell.id}
                align={'left'}
                padding={'normal'}
                sx={{
                  background: 'white',
                  paddingRight: `${index === columns.length - 1 ? 1 : 0}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: `${index === columns.length - 1 ? 'center' : 'space-between'}`,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: '24px',
                      fontStyle: 'normal',
                      fontWeight: 'medium',
                    }}
                  >
                    {headCell.label}
                  </Typography>
                  <Typography
                    sx={{
                      height: '14px',
                      width: '2px',
                      backgroundColor: `${
                        index === columns.length - 1 || index === columns.length - 2
                          ? 'white'
                          : '#E0E0E0'
                      }`,
                    }}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, index) => (
            <TableRow hover tabIndex={-1} key={index}>
              {columns.map((column, index) => (
                <TableCell key={index} align={column.align} sx={{ padding: '14px 16px' }}>
                  {column.render(item, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[]}
              rowsPerPage={pagination.limitValue}
              count={pagination.totalCount}
              page={page}
              onPageChange={handleChangePage}
              sx={{
                fontSize: 12,
                fontStyle: 'normal',
                lineHeight: '19.92px',
                fontWeight: 400,
                borderColor: 'white',
              }}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}
